import { getSharedStyleSheet } from './sharedStyleSheet.js';

class DialogBox extends HTMLElement {
    static observedAttributes = ['dialog-title', 'width', 'height', 'noblur', 'show'];

    constructor() {
        super();
        this._onCloseHandler = null;
        this._autoKill = true;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();

        if (attrName === "show" && oldValue !== newValue) {
            if (!!newValue) {
                this.show();
            } else {
                this.close();
            }
        }
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.movedByUser = false;
            this.attachShadow({mode: 'open'});

            this.shadowRoot.host.style.display = 'block';

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._bg = document.createElement('div');
            this._bg.className = 'dialog-background';
            this._bg.style.display = 'none';
            this.shadowRoot.appendChild(this._bg);

            // Unique id so multiple dialogs on a page don't collide aria-labelledby
            this._titleId = `dialog-title-${Math.random().toString(36).slice(2, 10)}`;

            const wrapper = `<div class="dialog-wrapper" role="dialog" aria-modal="true" aria-labelledby="${this._titleId}" tabindex="-1" style="display: none"></div>`;

            this._bg.insertAdjacentHTML('beforeend', wrapper);
            this._wrapper = this._bg.querySelector('.dialog-wrapper');

            this._wrapper.addEventListener('keydown', (e) => {
                if (e.target.role === 'dialog') {
                    const xRe = /translateX\((-?[0-9]+)px\)/;
                    let xTranslate = parseInt(e.target.style.transform?.match(xRe)?.[1] || 0);
                    const yRe = /translateY\((-?[0-9]+)px\)/;
                    let yTranslate = parseInt(e.target.style.transform?.match(yRe)?.[1] || 0);

                    switch (e.key) {
                        case 'ArrowLeft':
                            e.preventDefault();
                            xTranslate -= 5;
                            break;

                        case 'ArrowRight':
                            e.preventDefault();
                            xTranslate += 5;
                            break;

                        case 'ArrowUp':
                            e.preventDefault();
                            yTranslate -= 5;
                            break;

                        case 'ArrowDown':
                            e.preventDefault();
                            yTranslate += 5;
                            break;
                    }

                    e.target.style.transform = `translateX(${xTranslate}px) translateY(${yTranslate}px)`;
                }
            });

            let titlebar = `<div class="dialog-title-bar">`;
            titlebar += `<div class="dialog-title-bar-text" id="${this._titleId}"></div>`;
            titlebar += `<button type="button" aria-label="Close" title="Close" class="dialog-title-bar-close-button img-button"><div class="fa-icon fa-icon-x"></div></button>`;
            titlebar += `</div>`;

            this._wrapper.insertAdjacentHTML('beforeend', titlebar);
            this._titlebar = this._wrapper.querySelector('.dialog-title-bar');

            this._titlebarCloseButton = this._titlebar.querySelector('.dialog-title-bar-close-button');

            const contentWrapper = `<div class="dialog-content"></div>`;

            this._wrapper.insertAdjacentHTML('beforeend', contentWrapper);
            this._contentWrapper = this._wrapper.querySelector('.dialog-content');
            if (!!this.getAttribute('noscroll')) this._contentWrapper.style.overflowY = 'hidden';

            const content = `<slot id="dialog-content" name="content"></slot>`;

            this._contentWrapper.insertAdjacentHTML('beforeend', content);
            this._content = this._contentWrapper.querySelector('#dialog-content');

            const buttonBar = `<div class="dialog-button-bar"></div>`;

            this._wrapper.insertAdjacentHTML('beforeend', buttonBar);
            this._buttonBar = this._wrapper.querySelector('.dialog-button-bar');

            const buttons = `<slot id="dialog-buttons" name="buttons"></slot>`;

            this._buttonBar.insertAdjacentHTML('beforeend', buttons);
            this._buttons = this._buttonBar.querySelector('#dialog-buttons');

            this.setupEvents();

            if (!!this.getAttribute('show')) {
                this.show();
            }
        }

        this.dynamicRender();
    }

    setupEvents() {
        // Window dragging
        this._titlebar.addEventListener('pointerdown', (e) => {
            if (!this._isDragging) {
                if (!!this.getAttribute('no-drag')) return;
                this._isDragging = true;
                this._dragOffsetX = e.clientX - this._wrapper.offsetLeft;
                this._dragOffsetY = e.clientY - this._wrapper.offsetTop;
            }
        });

        this._bg.addEventListener('pointerup', (e) => {
            if (!!this.getAttribute('no-drag')) return;
            this._isDragging = false;
        });

        this._bg.addEventListener('pointermove', (e) => {
            if (!!this.getAttribute('no-drag')) return;
            if (!this._isDragging) return;
            e.preventDefault();

            this._wrapper.style.left = (e.clientX - this._dragOffsetX) + 'px';
            this._wrapper.style.top = (e.clientY - this._dragOffsetY) + 'px';
        });

        // Close events
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (!!this.getAttribute('no-close')) return;

                e.preventDefault();
                this.close();
            }
        });

        // Focus trap — keep Tab navigation inside the dialog while it's open
        this._wrapper.addEventListener('keydown', (e) => {
            if (e.key !== 'Tab') return;
            const focusables = this._getFocusableElements();
            if (focusables.length === 0) {
                e.preventDefault();
                this._wrapper.focus();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = this.shadowRoot.activeElement || document.activeElement;
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        });

        this._titlebarCloseButton.addEventListener('click', (e) => {
            if (!!this.getAttribute('no-close')) return;
            this.close();
        });

        // Prevent background from scrolling
        this._bg.addEventListener('wheel', (e) => {
            if (e.target.className === 'dialog-background') e.preventDefault();
        });
    }

    dynamicRender() {
        if (!this._titlebar || !this._wrapper) return;

        const titleBarText = this._titlebar.querySelector('.dialog-title-bar-text');

        if (this.getAttribute('dialog-title')) {
            titleBarText.innerHTML = this.getAttribute('dialog-title');
        } else {
            titleBarText.innerHTML = 'Dialog';
        }

        if (this.getAttribute('width')) {
            this._wrapper.style.width = this.getAttribute('width') + 'px';
        }

        if (this.getAttribute('height')) {
            this._wrapper.style.height = this.getAttribute('height') + 'px';
        }

        // Center align
        if (!this.movedByUser) {
            const pageWidth = window.innerWidth;
            const pageHeight = window.innerHeight;

            let leftPos = (pageWidth - this._wrapper.clientWidth) / 2;
            let topPos = (pageHeight - this._wrapper.clientHeight) / 2;

            this._wrapper.style.left = leftPos + 'px';
            this._wrapper.style.top = topPos + 'px';
        }

        // Disable background if noblur specified
        if (this.getAttribute('noblur')) {
            if (!this._bg.classList.contains('noblur')) {
                this._bg.classList.add('noblur');
            }
        } else {
            if (this._bg.classList.contains('noblur')) {
                this._bg.classList.add('noblur');
            }
        }

        // Disable close button if no-close set
        if (!!this.getAttribute('no-close')) {
            this._titlebarCloseButton.style.display = 'none';
        } else {
            this._titlebarCloseButton.style.display = 'block';
        }

    }

    show() {
        if (!this._bg || !this._wrapper) return;

        // Remember the element that opened the dialog so we can restore focus on close
        this._previouslyFocused = document.activeElement;

        this.movedByUser = false;
        this._bg.style.display = 'block';
        this._wrapper.style.display = 'flex';
        this.dynamicRender();

        // Move focus into the dialog. Prefer an element flagged with the
        // autofocus attribute (native autofocus only fires on initial page
        // parse, not when an already-parsed element is revealed via show()),
        // then fall back to the first focusable element, then the dialog itself.
        // preventScroll keeps the page from jumping when the focus target's
        // bounding rect is outside the viewport — the dialog is already
        // centred via JS positioning, so we don't want the browser scrolling
        // to bring its focus target into view.
        const focusables = this._getFocusableElements();
        const autofocusTarget = focusables.find(el => el.hasAttribute('autofocus'));
        if (autofocusTarget) {
            autofocusTarget.focus({ preventScroll: true });
        } else if (focusables.length > 0) {
            focusables[0].focus({ preventScroll: true });
        } else {
            this._wrapper.focus({ preventScroll: true });
        }
    }

    close() {
        if (!this._bg) return;

        this._bg.style.display = 'none';
        this._wrapper.style.display = 'none';

        if (this.onCloseHandler) {
            this.onCloseHandler();
        }

        // Restore focus to the element that opened the dialog
        if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
            this._previouslyFocused.focus({ preventScroll: true });
        }
        this._previouslyFocused = null;

        if (this._autoKill) {
            this.shadowRoot.host.remove();
        }
    }

    // Return all focusable descendants in tab order, including any slotted content
    _getFocusableElements() {
        const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const result = [];

        // Elements inside the shadow root (close button + button bar slots)
        this._wrapper.querySelectorAll(selector).forEach(el => {
            if (!el.disabled && el.offsetParent !== null) result.push(el);
        });

        // Slotted content from the light DOM (dialog content + buttons)
        const slots = this._wrapper.querySelectorAll('slot');
        slots.forEach(slot => {
            slot.assignedElements({ flatten: true }).forEach(node => {
                if (node.matches && node.matches(selector) && !node.disabled) {
                    result.push(node);
                }
                if (node.querySelectorAll) {
                    node.querySelectorAll(selector).forEach(el => {
                        if (!el.disabled) result.push(el);
                    });
                }
            });
        });

        return result;
    }

    get onCloseHandler()
    {
        return this._onCloseHandler;
    }

    set onCloseHandler(f)
    {
        this._onCloseHandler = f;
    }

    get autoKill()
    {
        return this._autoKill;
    }

    set autoKill(b)
    {
        this._autoKill = b;
    }
}

customElements.define('dialog-box', DialogBox);

